
/**
 * Utility for handling currency conversion based on user preferences stored in localStorage.
 */

export const getCurrencySettings = () => {
    try {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        if (userInfo && userInfo.user) {
            return {
                viewCurrency: userInfo.user.view_currency || 'AED',
                usdToAedRate: parseFloat(userInfo.user.usd_to_aed) || 3.67
            };
        }
    } catch (e) {
        console.error("Error reading currency settings", e);
    }
    return { viewCurrency: 'AED', usdToAedRate: 3.67 };
};

/**
 * Converts an amount from its original currency to the user's preferred viewing currency.
 * @param {number} amount - The amount to convert.
 * @param {string} originalCurrency - The currency of the amount (e.g., 'AED', 'USD'). Defaults to 'AED'.
 * @returns {number} - The converted amount.
 */
export const convertAmount = (amount, originalCurrency = 'AED') => {
    const { viewCurrency, usdToAedRate } = getCurrencySettings();
    const numAmount = parseFloat(amount) || 0;
    const cleanOriginal = (originalCurrency || 'AED').toUpperCase();
    const cleanTarget = (viewCurrency || 'AED').toUpperCase();

    if (cleanOriginal === cleanTarget) {
        return numAmount;
    }

    // Convert from AED to USD
    if (cleanOriginal === 'AED' && cleanTarget === 'USD') {
        return numAmount / usdToAedRate;
    }

    // Convert from USD to AED
    if (cleanOriginal === 'USD' && cleanTarget === 'AED') {
        return numAmount * usdToAedRate;
    }

    // Default: return amount if currencies are not handled
    return numAmount;
};

/**
 * Formats an amount with the user's preferred currency symbol/code.
 * @param {number} amount - The converted amount.
 * @param {boolean} useSymbol - Whether to use symbols ($) or codes (USD).
 * @returns {string} - Formatted string (e.g., "$ 1,234.56" or "1,234.56 AED")
 */
export const formatCurrency = (amount, useSymbol = true) => {
    const { viewCurrency } = getCurrencySettings();
    const formattedNum = Number(amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    if (viewCurrency === 'USD') {
        return useSymbol ? `$ ${formattedNum}` : `${formattedNum} USD`;
    } else {
        return useSymbol ? `AED ${formattedNum}` : `${formattedNum} AED`;
    }
};

/**
 * A combined helper that converts and formats in one go.
 */
export const displayConvertedAmount = (amount, originalCurrency = 'AED') => {
    const converted = convertAmount(amount, originalCurrency);
    return formatCurrency(converted);
};
